import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Link } from "react-router-dom";
import AffiliateDisclosure from "@/components/AffiliateDisclosure";
import ComparisonTable from "@/components/ComparisonTable";
import ProductCard from "@/components/ProductCard";
import EmailCapture from "@/components/EmailCapture";
import SEOHead from "@/components/SEOHead";
import TableOfContents from "@/components/TableOfContents";
import { ArrowRight, ChevronRight } from "lucide-react";

const TOC = [
  { id: "comparison", label: "Quick Comparison Table" },
  { id: "reviews", label: "Detailed Reviews" },
  { id: "buying-tips", label: "RV Solar Buying Tips" },
];

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Best Solar Panels for RV (2026)",
  "description": "Find the best solar panels for RV use including rigid roof panels, flexible panels for curved roofs, and portable options for boondocking.",
  "url": "https://highestefficiencysolarcell.com/best-solar-panels-for-rv",
  "dateModified": "2026-06-01",
  "author": { "@type": "Organization", "name": "HighestEfficiencySolarCell.com" },
};

export default function BestRVPanels() {
  const { data: products = [] } = useQuery({
    queryKey: ["solar-products"],
    queryFn: () => base44.entities.SolarProduct.list("sort_order", 50),
  });

  const rvProducts = products.filter(
    (p) => p.best_for_category === "Best Solar Panel for RV" || 
           p.best_use_case?.toLowerCase().includes("rv")
  );

  const displayProducts = rvProducts.length > 0 ? rvProducts : products.slice(0, 6);

  return (
    <>
      <SEOHead
        title="Best Solar Panels for RV 2026 | Rigid, Flexible & Portable Picks"
        description="Find the best solar panels for your RV — rigid roof panels, flexible panels for curved roofs, and portable options for boondocking. Updated 2026 buyer's guide."
        canonical="https://highestefficiencysolarcell.com/best-solar-panels-for-rv"
        schema={SCHEMA}
      />
    <div className="pb-28 lg:pb-0">
      <div className="max-w-7xl mx-auto px-4 py-12 lg:py-20">
        <nav className="flex items-center gap-1.5 text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-accent">Home</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-foreground">Best Solar Panels for RV</span>
        </nav>

        <div className="max-w-4xl mb-2">
          <h1 className="font-heading font-bold text-4xl lg:text-5xl mb-4">
            Best Solar Panels for RV (2026)
          </h1>
          <p className="text-lg text-muted-foreground mb-6 max-w-3xl">
          Whether you need rigid rooftop panels, flexible panels for a curved roof, or portable panels 
          for boondocking, here are the best high-efficiency solar panels for RV use.
        </p>

          <AffiliateDisclosure />
          <TableOfContents items={TOC} />
        </div>

        <section id="comparison" className="mb-8">
          <h2 className="font-heading font-bold text-3xl mb-4">Quick Comparison Table</h2>
        </section>

        {displayProducts.length > 0 && (
          <>
            <ComparisonTable products={displayProducts} />
            <div className="mt-16" id="reviews">
              <h2 className="font-heading font-bold text-3xl mb-8">Detailed RV Solar Panel Reviews</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {displayProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            </div>
          </>
        )}

        <div className="mt-16 max-w-3xl" id="buying-tips">
          <h2 className="font-heading font-bold text-2xl mb-6">RV Solar Panel Buying Guide</h2>
          <div className="space-y-5 text-muted-foreground">
            <p>
              <strong className="text-foreground">Measure your usable roof space first.</strong> RV roofs have
              vents, AC units, antennas, and skylights that limit panel placement. Draw a rough diagram of your
              roof and identify the clear, flat areas. A 100W rigid panel is typically 21" × 47" — know how many
              fit before you shop. Don't forget clearance around roof edges.
            </p>
            <p>
              <strong className="text-foreground">How much solar do you actually need?</strong> The right wattage
              depends on your camping style. Weekend campers with basic loads (lights, phone, fan) typically need
              200–400W. Full-time boondockers running a 12V refrigerator, laptop, TV, and CPAP typically need
              600–1200W. Use our <Link to="/solar-panel-calculator" className="text-accent-text underline underline-offset-2">solar panel calculator</Link> to
              get a precise estimate for your load profile.
            </p>
            <p>
              <strong className="text-foreground">Always use an MPPT charge controller.</strong> MPPT controllers
              extract 15–30% more power from your panels compared to PWM controllers. For any system over 100W,
              MPPT is the correct choice. Renogy Rover, Victron SmartSolar, and Epever Tracer are the most
              popular options for RV use. Size your controller to handle your panel array's short-circuit current
              with a 25% safety margin.
            </p>
            <p>
              <strong className="text-foreground">Rigid vs flexible for your roof type.</strong> Rigid aluminum-framed
              panels are the best long-term choice for flat or near-flat RV roofs — they run cooler, last 25+ years,
              and produce more watts per dollar. Flexible panels make sense for significantly curved roofs (some
              teardrop trailers, Airstreams) where rigid panels won't sit flat. Flexible panels mounted directly to a
              hot metal roof lose significant output to heat — if you go flexible, leave a small air gap if possible.
            </p>
            <p>
              <strong className="text-foreground">Wiring: series or parallel?</strong> For 12V battery systems,
              wire panels in parallel (same voltage, additive current). For 24V systems with an MPPT controller,
              series wiring (additive voltage, lower current) reduces wire losses over long roof-to-battery runs
              and is generally preferred. Always match your wiring configuration to your controller's rated
              input voltage.
            </p>
            <p>
              <strong className="text-foreground">Consider a portable supplemental panel.</strong> Many experienced
              RVers mount 200–400W of rigid panels permanently on the roof and also carry a 100–200W foldable
              panel for campsite use. The portable panel can be positioned for optimal sun angle throughout the day,
              adding 30–50% more output vs fixed flat-roof panels during low-sun periods.
            </p>
          </div>

          <div className="mt-8 space-y-4">
            <h3 className="font-heading font-bold text-xl text-foreground">Frequently Asked Questions</h3>
            {[
              { q: "What solar panel wattage is enough for full-time RV living?", a: "For full-time RV living with a 12V refrigerator, laptop, lighting, phone charging, and a fan, most owners settle on 400–800W. Running rooftop AC on solar requires 1000W+ of solar and large battery storage — most full-timers use a generator supplement for high-draw AC use." },
              { q: "Should I get flexible or rigid solar panels for my RV?", a: "Rigid monocrystalline PERC or HJT panels are the better choice for most RVs. They run cooler, last longer (25+ years vs 5–10 for flexible), and produce more watts per dollar. Flexible panels are worth considering only if your roof has significant curvature that prevents rigid panel mounting." },
              { q: "How many 200W solar panels do I need for an RV?", a: "Two 200W panels (400W total) is a solid baseline for most Class B and Class C RVs running a 12V refrigerator and basic electronics. For larger Class A motorhomes or full-time boondocking, three to six 200W panels (600W–1200W) is more appropriate." },
            ].map((faq, i) => (
              <div key={i} className="border border-border rounded-xl p-5">
                <h3 className="font-heading font-semibold mb-2 text-foreground text-base">{faq.q}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 flex flex-col sm:flex-row gap-4">
          <Link
            to="/solar-panel-calculator"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-accent text-foreground font-mono font-bold text-sm rounded-lg hover:bg-accent/90 min-h-[48px]"
          >
            Calculate Your RV Solar Needs <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/flexible-solar-panels"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-border text-sm font-medium rounded-lg hover:bg-secondary min-h-[48px]"
          >
            Flexible vs Rigid Guide
          </Link>
        </div>
      </div>
      <EmailCapture source="rv-panels" />
    </div>
    </>
  );
}